/**
 * SendErrorBanner — transient send-error banner that self-dismisses after 3s.
 *
 * Flutter source of truth: `retrySendingMessageWidget`
 * (`chat_screen.dart:3461-3522`) and the 3s auto-hide timer at
 * `chat_screen.dart:706-712`.
 *
 * Behavior in Flutter:
 *  - `Visibility(visible: _errorSendingMessage && hasInternetConnectivity, ...)`
 *    — the parent gates rendering. This component does not own the
 *    "should we show this at all" predicate.
 *  - When a send fails, the parent flips `_errorSendingMessage = true` and
 *    schedules a `Future.delayed(3s)` that flips it back to `false`.
 *  - Tapping the banner also flips `_errorSendingMessage` back to `false`.
 *  - Visually: centered pill near the bottom (Flutter uses
 *    `Padding(bottom: 70)` to clear the composer), rounded 14px, black
 *    background, red text, capped at 50% of viewport width.
 *
 * Web contract:
 *  - Parent decides when to mount (e.g. `errorSendingMessage && hasInternet`).
 *    Mounting the component activates it; unmounting hides it.
 *  - On mount, this component starts a 3s timer and calls `onDismiss` when it
 *    fires (parent should respond by unmounting / flipping its predicate to
 *    false — matching the Flutter `_errorSendingMessage = false` setState).
 *  - Tap also calls `onDismiss` (matches `GestureDetector.onTap` at
 *    `chat_screen.dart:3479-3482`).
 *  - The 3s timer is restarted whenever the consumer remounts the banner —
 *    i.e. give it a `key` tied to the error event id if you want successive
 *    errors to each get a full 3s window. If the consumer keeps the banner
 *    mounted across errors without a key change, the timer only fires once;
 *    that mirrors the Flutter behavior where successive failures within the
 *    3s window do not extend the visibility.
 *
 * UI port (Flutter parity for the shell, web idioms where they exist):
 *  - The vertical placement (Flutter's `bottom: 70` padding) is owned by the
 *    chat page's layout, not by this component. The component renders an
 *    overlay row centered horizontally with a top margin equal to whatever
 *    its parent provides; the parent positions it above the composer.
 *  - Background uses `theme.black`; text uses `theme.activeRed`. No
 *    hardcoded color literals.
 *  - Corner radius is `responsive.borderRadius.lg` (= 16 on mobile; Flutter's
 *    14 maps to the nearest token on the responsive scale). This is a
 *    deliberate small UI deviation per `presentation.md`: project-wide
 *    consistency beats per-component pixel parity.
 *  - Inner padding uses `responsive.spacing.base` vertically (= 12px, exact
 *    Flutter parity) and horizontally (Flutter switches between 40px and 12px
 *    based on content length; the copy here is well over 10 chars so the
 *    12px branch is always taken — we just use 12px directly).
 *  - Font uses `responsive.font.lg`. Flutter uses 15px with `AutoSizeText`
 *    `minFontSize: 15`; the responsive lg token (16px on mobile, scaling on
 *    larger breakpoints) keeps the typography on the project's scale per
 *    `presentation.md`. Marked as a deliberate UI deviation.
 *  - Max width is 50% of the viewport, matching the Flutter
 *    `MediaQuery.of(context).size.width / 2` constraint at
 *    `chat_screen.dart:3484-3486`.
 *
 * l10n: `chat.errorSendingMessage` (sourced from
 *       `useLocale().l10n.chat.errorSendingMessage`).
 */

import React, { useEffect } from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { ANIMATION_CONSTANTS } from '@/presentation/constants/animationConstants';
import { qaAttr } from '@/presentation/utils/qaAttr';

export interface SendErrorBannerProps {
  /**
   * Called when the banner should be dismissed — either because the user
   * tapped it (Flutter `GestureDetector.onTap`, `chat_screen.dart:3479-3482`)
   * or because the 3s auto-hide timer fired (Flutter `Future.delayed`,
   * `chat_screen.dart:706-712`). The parent typically responds by flipping
   * its `errorSendingMessage` predicate to `false`, unmounting the banner.
   */
  onDismiss: () => void;
}

export const SendErrorBanner: React.FC<SendErrorBannerProps> = ({
  onDismiss,
}) => {
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();
  const chat = l10n.chat;

  // Schedule the 3s auto-hide on mount (mirrors Flutter's
  // `Future.delayed(const Duration(seconds: 3))` at `chat_screen.dart:707`).
  // If the parent unmounts us early (e.g. because the user tapped to dismiss),
  // the cleanup clears the pending timer so we don't fire `onDismiss` against
  // a stale closure.
  useEffect(() => {
    const timerId = window.setTimeout(() => {
      onDismiss();
    }, ANIMATION_CONSTANTS.duration.autoHideDelay);
    return () => {
      window.clearTimeout(timerId);
    };
  }, [onDismiss]);

  return (
    <div
      {...qaAttr({ id: 'chat-send-error-banner' })}
      style={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        // The parent owns vertical placement (Flutter's `bottom: 70` padding
        // is at the parent column, not on the banner itself). This wrapper
        // only centers the pill horizontally.
        pointerEvents: 'none',
      }}
    >
      <button
        type="button"
        {...qaAttr({ id: 'chat-send-retry' })}
        onClick={onDismiss}
        style={{
          // Cap at 50% of the viewport, matching Flutter's
          // `MediaQuery.of(context).size.width / 2` constraint.
          maxWidth: '50vw',
          backgroundColor: theme.black,
          color: theme.activeRed,
          borderRadius: responsive.borderRadius.lg,
          paddingTop: responsive.spacing.base,
          paddingBottom: responsive.spacing.base,
          paddingLeft: responsive.spacing.base,
          paddingRight: responsive.spacing.base,
          border: 'none',
          textAlign: 'center',
          fontSize: responsive.font.lg,
          fontWeight: responsive.fontWeight.regular,
          cursor: 'pointer',
          // Re-enable pointer events on the pill itself; the outer row
          // disables them so the pill doesn't intercept clicks on
          // surrounding chrome (composer, message list edges).
          pointerEvents: 'auto',
        }}
      >
        {chat.errorSendingMessage}
      </button>
    </div>
  );
};
